import { FormikErrors, FormikTouched } from 'formik';
import { isEmpty } from 'lodash-es';
import * as Yup from 'yup';

import { Props as AutoCompleteProps } from '../AutoComplete';
import { RenderMode } from './Overseas';
import * as Interfaces from './interfaces';

function getSingleDetailsTouched(touched: boolean): FormikTouched<Interfaces.FullDetails> {
  return {
    fullAddress: touched,
    postCode: touched,
    state: touched,
    suburb: touched
  };
}

function getSingleDetailsErrors(error: string): FormikErrors<Interfaces.FullDetails> {
  return {
    fullAddress: error,
    postCode: error,
    state: error,
    suburb: error
  };
}

/**
 * Initial values object for <SingleAddress /> element
 */
export const initialSingleAddressValues: Interfaces.AddressData = {
  summaryData: {
    label: '',
    value: ''
  },
  fullDetails: { id: '', fullAddress: '', postCode: '', state: '', suburb: '' }
};

/**
 * Initial touched object for full <SingleAddress /> element
 * @param touched touched value to populate
 */
export const initialSingleAddressTouched = (touched: boolean) => {
  return {
    fullDetails: getSingleDetailsTouched(touched),
    /**
     * This can be of OptionType/number/string, hence we store a single boolean value in it for consistency across all different cases
     */
    summaryData: touched
  };
};

/**
 * Initial error object for <SingleAddress /> element
 * @param error error to populate
 */
export const initialSingleAddressErrors = (error: string) => {
  return {
    fullDetails: getSingleDetailsErrors(error),
    summaryData: error
  };
};

/**
 * Initial values object for an <Overseas /> element
 */
export const initialOverseasAddressValues: Interfaces.Overseas = {
  info: {
    basic: '',
    full: {
      fullAddress: '',
      postCode: '',
      state: '',
      suburb: ''
    }
  },
  country: ''
};

/**
 * Initial touched object for an <Overseas /> element
 * @param touched touched value to populate
 */
export const initialOverseasAddressTouched = (touched: boolean) => {
  return {
    country: touched,
    info: {
      basic: touched,
      full: {
        fullAddress: touched,
        postCode: touched,
        state: touched,
        suburb: touched
      }
    }
  };
};

/**
 * Initial error object for an <Overseas /> element
 * @param error error to populate
 */
export const initialOverseasAddressErrors = (error: string) => {
  return {
    overseas: {
      country: error,
      info: {
        basic: error,
        full: {
          fullAddress: error,
          postCode: error,
          state: error,
          suburb: error
        }
      }
    }
  };
};

/**
 * Does the given input address was entered as manual and not auto searched
 * @param value AddressData object to validate
 */
export const isManualAddress = (value: Interfaces.AddressData) => {
  return (
    isEmpty(value.fullDetails.id) &&
    !isEmpty(value.fullDetails.fullAddress) &&
    !isEmpty(value.fullDetails.postCode) &&
    !isEmpty(value.fullDetails.state) &&
    !isEmpty(value.fullDetails.suburb)
  );
};

/**
 * Manual type values
 */
export type ManualValues = SingleAddressFormValues;

/**
 * <Formik /> address type values defintion for a "single" type
 */
export type SingleAddressFormValues = {
  address: Interfaces.AddressData;
};

/**
 * <Formik /> address type values defintion for an array of "single" type
 */
export type SingleAddressArrayFormValues = {
  addresses: (Interfaces.AddressData & { id: string })[];
};

/**
 * <Formik /> address type values defnition for an "overseas" type
 */
export type OverseasAddressFormValues = {
  address: Interfaces.OverseasAddressData;
};

/**
 * <Formik /> address type values defnition for an array of an "overseas" type
 */
export type OverseasAddressArrayFormValues = {
  addresses: (Interfaces.OverseasAddressData & { id: string })[];
};
/**
 * standard <Formik /> values type definition for the <Multi /> element
 */
export type MultiAddressFormValues = {
  address: Interfaces.MultiAddressData;
};

/**
 * <Formik /> address type values defnition for an array of a <Multi /> type
 */
export type MultiAddressArrayFormValues = {
  addresses: (Interfaces.MultiAddressData & { id: string })[];
};

/**
 * Unique type for Form where the address is deeply nested inside the main <Formik /> "values" collection
 */
export type NestedAddressesFormValues = {
  multiAddresses: {
    first: Interfaces.MultiAddressData;
    second: Interfaces.MultiAddressData;
    unique: Interfaces.AddressData;
  };
};

/**
 * standard <Formik /> values type definition for an array of <Single /> elements
 */
export type ArrayFormValues = {
  addresses: Interfaces.AddressData[];
};

export const fullAddressSchema = Yup.object().shape<Interfaces.FullDetails>({
  fullAddress: Yup.string().required('Please enter a valid street address or PO Box.'),
  postCode: Yup.string()
    .required('Please enter a 4-digit postcode.')
    .min(4, 'Please enter a 4-digit postcode.'),
  state: Yup.string().required('Please select a State/Territory.'),
  suburb: Yup.string().required('Please enter a valid suburb.')
});

/**
 * type for the yup validation of "single" address schema
 */
export const addressDataSchema = Yup.object().shape<Interfaces.AddressData>({
  fullDetails: fullAddressSchema,
  summaryData: Yup.mixed()
    .test('emptyValue', 'Please enter a valid street address or PO Box.', function validate(
      address?: AutoCompleteProps.OptionType
    ) {
      const value = this.parent as Interfaces.AddressData;
      if (isManualAddress(value)) {
        return true;
      }
      return address ? !isEmpty(address.label) : false;
    })
    .test(
      'no value selected from drop down',
      "Please choose an address from the suggestions that appear below the address field. If you can't see your address listed, choose the last option and enter your address manually.",
      function validate(address?: AutoCompleteProps.OptionType) {
        const value = this.parent as Interfaces.AddressData;
        if (isManualAddress(value)) {
          return true;
        }
        return address ? !isEmpty(address.label) && !isEmpty(address.value) : false;
      }
    )
});

/**
 * Type for the yup validation of "mutli" address schema
 */
export type MultiAddressSchemaShape = Omit<Interfaces.MultiAddressData, 'isPostalSameAsHome'>;

/**
 * Multi address schema
 */
export const multiAddressSchema = Yup.object().shape<MultiAddressSchemaShape>({
  home: addressDataSchema,
  postal: addressDataSchema
});

export const overseasDataSchema = (mode: RenderMode) =>
  Yup.object().shape<Interfaces.Overseas>({
    country: Yup.string().required('We can’t help you without this information.'),
    info: Yup.object().shape<Interfaces.OverseasInformation>({
      basic:
        mode === 'Basic'
          ? Yup.string().required(
              'Please enter a valid street address or PO Box. We need this information to continue.'
            )
          : Yup.mixed().notRequired(),
      full: mode === 'Full' ? fullAddressSchema : Yup.mixed().notRequired()
    })
  });

/**
 * Multi address schema
 */
export const overseasAddressSchema = (mode: RenderMode) =>
  Yup.object().shape<Interfaces.OverseasAddressData>({
    local: Yup.mixed().when('isOverseasAddress', {
      is: val => val,
      then: Yup.mixed().notRequired(),
      otherwise: addressDataSchema
    }),
    isOverseasAddress: Yup.mixed().notRequired(),
    overseas: Yup.mixed().when('isOverseasAddress', {
      is: val => val,
      then: overseasDataSchema(mode),
      otherwise: Yup.mixed().notRequired()
    })
  });
